import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Project, GitHubRepo, ProjectTemplate, DatabaseHostConfig, ForgejoRepoInfo } from '@claude-code-api/shared';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  Search,
  X,
  Database,
  FolderGit2,
  FileCode,
  File,
  Lock,
  Globe,
  GitBranch,
  Compass,
} from 'lucide-react';
import { buildTemplateCatalogApiPath, getTemplateIconClassName, getTemplateIconComponent, getTemplateIconStyleAttribute } from '@/lib/template-catalog';

type ProjectSourceTab = 'templates' | 'github' | 'forgejo' | 'blank';

interface CreateProjectButtonProps {
  onProjectCreated: (project: Project) => void;
  buttonClassName?: string;
  buttonVariant?: 'default' | 'secondary' | 'outline' | 'ghost';
  autoOpenTemplateId?: string | null;
  onAutoOpenHandled?: () => void;
}

export function CreateProjectButton({
  onProjectCreated,
  buttonClassName = "gap-2 bg-teal-600 hover:bg-teal-700 text-white dark:bg-teal-500 dark:hover:bg-teal-600",
  buttonVariant = 'default',
  autoOpenTemplateId = null,
  onAutoOpenHandled,
}: CreateProjectButtonProps) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();

  // Modal state
  const [showNewProject, setShowNewProject] = useState(false);
  const [creating, setCreating] = useState(false);

  // Form state
  const [newProjectName, setNewProjectName] = useState('');
  const [projectNameError, setProjectNameError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ProjectSourceTab>('templates');
  const [createDatabase, setCreateDatabase] = useState(false);
  const [dbName, setDbName] = useState('');
  const [dbUsername, setDbUsername] = useState('');
  const [dbPassword, setDbPassword] = useState('');
  const [dbHostConfig, setDbHostConfig] = useState<DatabaseHostConfig | null>(null);

  // Templates state
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const [recentTemplateIds, setRecentTemplateIds] = useState<string[]>([]);
  const [pendingAutoTemplateId, setPendingAutoTemplateId] = useState<string | null>(null);
  const [pinnedTemplateId, setPinnedTemplateId] = useState<string | null>(null);

  // GitHub repos state
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');
  const [showRepoDropdown, setShowRepoDropdown] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [hasGithubToken, setHasGithubToken] = useState(false);

  // Forgejo repos state
  const [forgejoRepos, setForgejoRepos] = useState<ForgejoRepoInfo[]>([]);
  const [loadingForgejoRepos, setLoadingForgejoRepos] = useState(false);
  const [forgejoRepoSearch, setForgejoRepoSearch] = useState('');
  const [showForgejoDropdown, setShowForgejoDropdown] = useState(false);
  const [selectedForgejoRepo, setSelectedForgejoRepo] = useState<ForgejoRepoInfo | null>(null);

  const displayedTemplates = useMemo(() => {
    const templatesById = new Map(templates.map((template) => [template.id, template]));
    const orderedIds = [
      pinnedTemplateId,
      selectedTemplate?.id,
      ...recentTemplateIds,
    ].filter(Boolean) as string[];
    const uniqueIds = Array.from(new Set(orderedIds));
    return uniqueIds
      .map((id) => templatesById.get(id))
      .filter((template): template is ProjectTemplate => Boolean(template))
      .slice(0, 3);
  }, [pinnedTemplateId, recentTemplateIds, selectedTemplate?.id, templates]);

  // Fetch initial data when modal opens
  useEffect(() => {
    if (showNewProject) {
      fetchInitialData();
    }
  }, [showNewProject]);

  useEffect(() => {
    if (!autoOpenTemplateId) return;
    const nextTemplateId = autoOpenTemplateId === '__default__' ? null : autoOpenTemplateId;
    setPendingAutoTemplateId(nextTemplateId);
    setPinnedTemplateId(nextTemplateId);
    setShowNewProject(true);
    onAutoOpenHandled?.();
  }, [autoOpenTemplateId, onAutoOpenHandled]);

  const fetchInitialData = async () => {
    // Fetch GitHub connection status
    try {
      const response = await fetch('/api/auth/me', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setHasGithubToken(data.data?.hasGithubToken || false);
      }
    } catch (error) {
      console.error('Failed to check GitHub connection:', error);
      toast.error(t('dashboard:createProject.errors.loadGithubConnectionFailed'));
    }

    let nextRecentTemplateIds: string[] = [];
    try {
      const stored = JSON.parse(localStorage.getItem('gift.recentTemplateIds') || '[]') as unknown;
      const storedIds = Array.isArray(stored) ? stored.filter((value): value is string => typeof value === 'string') : [];
      const response = await fetch('/api/projects', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json() as { data?: Project[] };
        const projectTemplateIds = (data.data || [])
          .map((project) => project.templateId)
          .filter((value): value is string => Boolean(value));
        nextRecentTemplateIds = Array.from(new Set([...projectTemplateIds, ...storedIds]));
      } else {
        nextRecentTemplateIds = storedIds;
      }
      setRecentTemplateIds(nextRecentTemplateIds);
    } catch (error) {
      console.error('Failed to fetch recent templates:', error);
    }

    // Fetch templates
    try {
      const response = await fetch(buildTemplateCatalogApiPath('/api/templates', i18n.resolvedLanguage || i18n.language), { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const fetchedTemplates: ProjectTemplate[] = data.data || [];
        setTemplates(fetchedTemplates);
        const preferredTemplateId = pendingAutoTemplateId !== null
          ? pendingAutoTemplateId
          : nextRecentTemplateIds[0] || null;
        if (preferredTemplateId) {
          const preferredTemplate = fetchedTemplates.find((template) => template.id === preferredTemplateId) || null;
          setSelectedTemplate(preferredTemplate);
          if (preferredTemplate) {
            setCreateDatabase(true);
            setActiveTab('templates');
            if (pendingAutoTemplateId !== null) {
              setNewProjectName(preferredTemplate.name.toLowerCase().replace(/\s+/g, '-'));
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error(t('dashboard:createProject.errors.loadTemplatesFailed'));
    }

    // Fetch database host config
    try {
      const response = await fetch('/api/projects/database/host', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setDbHostConfig(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch database host:', error);
      toast.error(t('dashboard:createProject.errors.loadDatabaseConfigFailed'));
    }
  };

  const fetchRepos = async () => {
    setLoadingRepos(true);
    try {
      const response = await fetch('/api/github/repos', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRepos(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch repos:', error);
      toast.error(t('dashboard:createProject.errors.loadGithubReposFailed'));
    } finally {
      setLoadingRepos(false);
    }
  };

  const fetchForgejoRepos = async () => {
    setLoadingForgejoRepos(true);
    try {
      const response = await fetch('/api/forgejo/repos', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setForgejoRepos(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch Forgejo repos:', error);
      toast.error(t('dashboard:createProject.errors.loadForgejoReposFailed'));
    } finally {
      setLoadingForgejoRepos(false);
    }
  };

  const resetForm = () => {
    setNewProjectName('');
    setProjectNameError(null);
    setSelectedRepo(null);
    setSelectedTemplate(null);
    setSelectedForgejoRepo(null);
    setForgejoRepoSearch('');
    setRepoSearch('');
    setActiveTab('templates');
    setCreateDatabase(false);
    setDbName('');
    setDbUsername('');
    setDbPassword('');
    setPendingAutoTemplateId(null);
    setPinnedTemplateId(null);
  };

  const selectRepo = (repo: GitHubRepo) => {
    setSelectedRepo(repo);
    setNewProjectName(repo.name);
    setShowRepoDropdown(false);
    setRepoSearch('');
  };

  const selectTemplate = (template: ProjectTemplate) => {
    setSelectedTemplate(template);
    setCreateDatabase(true);
    if (!newProjectName) {
      setNewProjectName(template.name.toLowerCase().replace(/\s+/g, '-'));
    }
  };

  const openTemplateMarketplace = () => {
    setShowNewProject(false);
    navigate('/templates');
  };

  const clearSelectedRepo = () => {
    setSelectedRepo(null);
    setNewProjectName('');
  };

  const filteredRepos = repos.filter(repo =>
    repo.name.toLowerCase().includes(repoSearch.toLowerCase()) ||
    repo.fullName.toLowerCase().includes(repoSearch.toLowerCase())
  );

  const createProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newProjectName,
          gitUrl: activeTab === 'github' ? (selectedRepo?.cloneUrl || undefined) : undefined,
          templateId: activeTab === 'templates' ? (selectedTemplate?.id || undefined) : undefined,
          forgejoRepoFullName: activeTab === 'forgejo' ? (selectedForgejoRepo?.fullName || undefined) : undefined,
          createDatabase,
          dbName: createDatabase && dbName.trim() ? dbName.trim() : undefined,
          dbUsername: createDatabase && dbUsername.trim() ? dbUsername.trim() : undefined,
          dbPassword: createDatabase && dbPassword.trim() ? dbPassword.trim() : undefined,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        if (activeTab === 'templates' && selectedTemplate) {
          const nextRecentTemplateIds = Array.from(new Set([selectedTemplate.id, ...recentTemplateIds])).slice(0, 12);
          setRecentTemplateIds(nextRecentTemplateIds);
          localStorage.setItem('gift.recentTemplateIds', JSON.stringify(nextRecentTemplateIds));
        }
        onProjectCreated(data.data);
        setShowNewProject(false);
        resetForm();
      } else {
        const errorMsg = data.error || t('dashboard:createProject.errors.createProjectFailed');
        if (errorMsg.includes('name already exists') || errorMsg.includes('different project name')) {
          setProjectNameError(errorMsg);
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error(t('dashboard:createProject.errors.createProjectFailed'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowNewProject(true)}
        variant={buttonVariant}
        className={buttonClassName}
      >
        <Plus className="w-4 h-4" />
        {t('dashboard:createProject.button')}
      </Button>

      <Dialog open={showNewProject} onOpenChange={(open) => !creating && setShowNewProject(open)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('dashboard:createProject.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('dashboard:createProject.dialogDescription')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={createProject} className="space-y-6">
            {/* Source Selection Tabs */}
            <div>
              <Label className="mb-3 block">{t('dashboard:createProject.startFrom')}</Label>
              <Tabs value={activeTab} onValueChange={(v) => {
                setActiveTab(v as ProjectSourceTab);
                if (v === 'templates') { setSelectedRepo(null); setSelectedForgejoRepo(null); }
                if (v === 'github') {
                  setSelectedTemplate(null);
                  setSelectedForgejoRepo(null);
                  if (hasGithubToken && repos.length === 0) fetchRepos();
                }
                if (v === 'forgejo') {
                  setSelectedTemplate(null);
                  setSelectedRepo(null);
                  if (forgejoRepos.length === 0) fetchForgejoRepos();
                }
                if (v === 'blank') {
                  setSelectedRepo(null);
                  setSelectedTemplate(null);
                  setSelectedForgejoRepo(null);
                }
              }}>
                <TabsList className="w-full grid grid-cols-4">
                  <TabsTrigger value="templates" className="gap-2">
                    <FileCode className="w-4 h-4" />
                    {t('dashboard:createProject.tabs.templates')}
                  </TabsTrigger>
                  <TabsTrigger value="github" className="gap-2">
                    <FolderGit2 className="w-4 h-4" />
                    {t('dashboard:createProject.tabs.github')}
                  </TabsTrigger>
                  <TabsTrigger value="forgejo" className="gap-2">
                    <GitBranch className="w-4 h-4" />
                    {t('dashboard:createProject.tabs.forgejo')}
                  </TabsTrigger>
                  <TabsTrigger value="blank" className="gap-2">
                    <File className="w-4 h-4" />
                    {t('dashboard:createProject.tabs.blank')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Templates Tab Content */}
            {activeTab === 'templates' && (
              <div className="space-y-3">
                {displayedTemplates.length > 0 ? (
                  <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                    {displayedTemplates.map((template) => {
                      const TemplateIcon = getTemplateIconComponent(template);
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => selectTemplate(template)}
                          className={`p-4 text-left border-2 rounded-xl transition-all ${
                            selectedTemplate?.id === template.id
                              ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                              : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getTemplateIconClassName(template)}`} style={getTemplateIconStyleAttribute(template)}>
                              <TemplateIcon className="w-6 h-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold">{template.name}</p>
                              <p className="text-sm text-muted-foreground truncate">{template.description}</p>
                            </div>
                            {selectedTemplate?.id === template.id && (
                              <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={openTemplateMarketplace}
                      className="p-4 text-left border-2 border-dashed rounded-xl transition-all hover:border-muted-foreground/40 hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center text-muted-foreground">
                          <Compass className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold">{t('dashboard:createProject.discoverTemplates')}</p>
                          <p className="text-sm text-muted-foreground truncate">{t('dashboard:createProject.discoverTemplatesDescription')}</p>
                        </div>
                      </div>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={openTemplateMarketplace}
                    className="w-full p-6 text-left border-2 border-dashed rounded-xl transition-all hover:border-muted-foreground/40 hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center text-muted-foreground">
                        <Compass className="w-6 h-6" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold">{t('dashboard:createProject.discoverTemplates')}</p>
                        <p className="text-sm text-muted-foreground">{t('dashboard:createProject.noRecentTemplates')}</p>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            )}

            {/* GitHub Repo Tab Content */}
            {activeTab === 'github' && (
              <div>
                {hasGithubToken ? (
                  <>
                    {selectedRepo ? (
                      <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-foreground flex items-center justify-center">
                            <FolderGit2 className="w-6 h-6 text-background" />
                          </div>
                          <div>
                            <p className="font-medium">{selectedRepo.fullName}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {selectedRepo.isPrivate ? (
                                <Badge variant="outline" className="gap-1">
                                  <Lock className="w-3 h-3" />
                                  {t('dashboard:createProject.private')}
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="gap-1">
                                  <Globe className="w-3 h-3" />
                                  {t('dashboard:createProject.public')}
                                </Badge>
                              )}
                              {selectedRepo.language && (
                                <span className="text-xs text-muted-foreground">{selectedRepo.language}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={clearSelectedRepo}
                          aria-label={t('dashboard:createProject.clearSelection')}
                        >
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="text"
                            value={repoSearch}
                            onChange={(e) => {
                              setRepoSearch(e.target.value);
                              setShowRepoDropdown(true);
                            }}
                            onFocus={() => setShowRepoDropdown(true)}
                            placeholder={t('dashboard:createProject.searchReposPlaceholder')}
                            className="pl-10 pr-10 py-6"
                          />
                          {loadingRepos && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <Spinner className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        {showRepoDropdown && !loadingRepos && (
                          <div className="absolute z-10 w-full mt-2 bg-popover border rounded-xl shadow-lg max-h-64 overflow-auto">
                            {filteredRepos.length === 0 ? (
                              <div className="px-4 py-8 text-center text-muted-foreground">
                                <FolderGit2 className="mx-auto h-8 w-8 mb-2" />
                                <p className="text-sm">{repoSearch ? t('dashboard:createProject.noReposFound') : t('dashboard:createProject.noReposAvailable')}</p>
                              </div>
                            ) : (
                              filteredRepos.map((repo) => (
                                <button
                                  key={repo.id}
                                  type="button"
                                  onClick={() => selectRepo(repo)}
                                  className="w-full px-4 py-3 text-left hover:bg-muted flex items-center justify-between border-b last:border-0 transition-colors"
                                >
                                  <div className="min-w-0">
                                    <p className="font-medium truncate">{repo.name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{repo.fullName}</p>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                    {repo.isPrivate && (
                                      <Badge variant="outline" className="text-xs">{t('dashboard:createProject.private')}</Badge>
                                    )}
                                    {repo.language && (
                                      <span className="text-xs text-muted-foreground">{repo.language}</span>
                                    )}
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <a
                    href="/api/auth/github/link"
                    className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-foreground text-background rounded-xl hover:bg-foreground/90 transition-colors"
                  >
                    <FolderGit2 className="w-6 h-6" />
                    <span className="font-medium">{t('dashboard:createProject.connectGithub')}</span>
                  </a>
                )}
              </div>
            )}

            {/* Forgejo Repo Tab Content */}
            {activeTab === 'forgejo' && (
              <div>
                {selectedForgejoRepo ? (
                  <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                        <GitBranch className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">{selectedForgejoRepo.fullName}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {selectedForgejoRepo.private ? (
                            <Badge variant="outline" className="gap-1">
                              <Lock className="w-3 h-3" />
                              {t('dashboard:createProject.private')}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <Globe className="w-3 h-3" />
                              {t('dashboard:createProject.public')}
                            </Badge>
                          )}
                          {selectedForgejoRepo.description && (
                            <span className="text-xs text-muted-foreground truncate max-w-[200px]">{selectedForgejoRepo.description}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => { setSelectedForgejoRepo(null); setNewProjectName(''); }}
                      aria-label={t('dashboard:createProject.clearSelection')}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="text"
                        value={forgejoRepoSearch}
                        onChange={(e) => {
                          setForgejoRepoSearch(e.target.value);
                          setShowForgejoDropdown(true);
                        }}
                        onFocus={() => setShowForgejoDropdown(true)}
                        placeholder={t('dashboard:createProject.searchForgejoPlaceholder')}
                        className="pl-10 pr-10 py-6"
                      />
                      {loadingForgejoRepos && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Spinner className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                    {showForgejoDropdown && !loadingForgejoRepos && (
                      <div className="absolute z-10 w-full mt-2 bg-popover border rounded-xl shadow-lg max-h-64 overflow-auto">
                        {(() => {
                          const filtered = forgejoRepos.filter(repo =>
                            repo.name.toLowerCase().includes(forgejoRepoSearch.toLowerCase()) ||
                            repo.fullName.toLowerCase().includes(forgejoRepoSearch.toLowerCase())
                          );
                          return filtered.length === 0 ? (
                            <div className="px-4 py-8 text-center text-muted-foreground">
                              <GitBranch className="mx-auto h-8 w-8 mb-2" />
                              <p className="text-sm">{forgejoRepoSearch ? t('dashboard:createProject.noForgejoReposFound') : t('dashboard:createProject.noForgejoReposAvailable')}</p>
                            </div>
                          ) : (
                            filtered.map((repo) => (
                              <button
                                key={repo.id}
                                type="button"
                                onClick={() => {
                                  setSelectedForgejoRepo(repo);
                                  setNewProjectName(repo.name);
                                  setShowForgejoDropdown(false);
                                  setForgejoRepoSearch('');
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-muted flex items-center justify-between border-b last:border-0 transition-colors"
                              >
                                <div className="min-w-0">
                                  <p className="font-medium truncate">{repo.name}</p>
                                  {repo.description && (
                                    <p className="text-xs text-muted-foreground truncate">{repo.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                  {repo.private && (
                                    <Badge variant="outline" className="text-xs">{t('dashboard:createProject.private')}</Badge>
                                  )}
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(repo.updatedAt).toLocaleDateString()}
                                  </span>
                                </div>
                              </button>
                            ))
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Blank Project Tab Content */}
            {activeTab === 'blank' && (
              <div className="py-8 text-center">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <File className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-foreground">{t('dashboard:createProject.blankTitle')}</p>
                <p className="text-sm text-muted-foreground mt-1">{t('dashboard:createProject.blankDescription')}</p>
              </div>
            )}

            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="projectName">{t('dashboard:createProject.projectName')}</Label>
              <Input
                id="projectName"
                type="text"
                value={newProjectName}
                onChange={(e) => {
                  setNewProjectName(e.target.value);
                  setProjectNameError(null);
                }}
                placeholder={t('dashboard:createProject.projectNamePlaceholder')}
                className={`py-6 ${projectNameError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                required
              />
              {projectNameError && (
                <p className="text-sm text-destructive">{projectNameError}</p>
              )}
            </div>

            {/* Database Option */}
            <div className="p-4 bg-muted/50 rounded-xl border">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="createDatabase"
                  checked={createDatabase}
                  onCheckedChange={(checked) => setCreateDatabase(checked === true)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="createDatabase" className="cursor-pointer flex items-center gap-2">
                    <Database className="w-4 h-4 text-success" />
                    {t('dashboard:createProject.createDatabase')}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('dashboard:createProject.createDatabaseDescription')}
                  </p>
                  {dbHostConfig && (
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {t('dashboard:createProject.dbHost')}: <span className="font-mono">{dbHostConfig.host}:{dbHostConfig.port}</span>
                    </p>
                  )}
                </div>
              </div>

              {createDatabase && (
                <div className="mt-4 ml-8 space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="dbName" className="text-xs">
                      {t('dashboard:createProject.dbName')} <span className="text-muted-foreground">({t('dashboard:createProject.optional')})</span>
                    </Label>
                    <Input
                      type="text"
                      id="dbName"
                      value={dbName}
                      onChange={(e) => setDbName(e.target.value)}
                      placeholder={t('dashboard:createProject.autoGeneratedIfEmpty')}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dbUsername" className="text-xs">
                      {t('dashboard:createProject.dbUsername')} <span className="text-muted-foreground">({t('dashboard:createProject.optional')})</span>
                    </Label>
                    <Input
                      type="text"
                      id="dbUsername"
                      value={dbUsername}
                      onChange={(e) => setDbUsername(e.target.value)}
                      placeholder={t('dashboard:createProject.autoGeneratedIfEmpty')}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dbPassword" className="text-xs">
                      {t('dashboard:createProject.dbPassword')} <span className="text-muted-foreground">({t('dashboard:createProject.optional')})</span>
                    </Label>
                    <Input
                      type="password"
                      id="dbPassword"
                      value={dbPassword}
                      onChange={(e) => setDbPassword(e.target.value)}
                      placeholder={t('dashboard:createProject.autoGeneratedIfEmpty')}
                      className="h-9"
                    />
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowNewProject(false);
                  resetForm();
                }}
                disabled={creating}
              >
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? (
                  <>
                    <Spinner className="w-4 h-4 mr-2" />
                    {t('dashboard:createProject.creating')}
                  </>
                ) : (
                  t('dashboard:createProject.button')
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
